/**
 * v2 Bulk Jobs — submit form + list view.
 *
 * The submit form fans a single job across many chassis at once
 * via ``POST /api/bulk-jobs`` (typed-confirm gated server-side).
 * The history table below it lists recent parent bulk_dispatch
 * jobs; click any row to see the per-child fan-out.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileSearch, Play } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { api } from "../../../api/client";
import { LocalTime } from "../../../lib/datetime";
import { Badge, type BadgeTone } from "../../components/Badge";
import {
  type BulkJobDetailView,
  type BulkJobDetailWire,
  ChildJobsTable,
  normaliseBulkJobDetail,
} from "../../components/ChildJobsTable";
import { CommandPreview } from "../../components/CommandPreview";
import { useConfirm } from "../../components/ConfirmDialog";
import { CopyableCell } from "../../components/CopyableCell";
import { useToast } from "../../components/Toast";
import { HelpButton } from "../../help/HelpPanel";

/** Wire shape of ``GET /bulk-jobs`` (``BulkJobListRow`` in
 *  apps/web/app/schemas/bulk_jobs.py). */
type BulkJobRow = {
  parent_job_id: string;
  kind: string;
  status: string;
  started_by_gigafy_username: string | null;
  started_at: string | null;
  completed_at: string | null;
  total_targets: number | null;
};

type BulkListResponse = { bulk_jobs: BulkJobRow[] };

const STATUS_TONE: Record<string, BadgeTone> = {
  queued: "neutral",
  running: "info",
  succeeded: "success",
  failed: "danger",
  partial: "warning",
};

// Common bulk-job kinds. Other names still work via the "Other…"
// override; this list is just the menu for the 99% case.
const COMMON_KINDS = [
  "list_onus",
  "list_pending_onus",
  "collect_mac",
  "collect_optical",
  "parse_alarms",
  "backup_running_config",
  "verify_config",
  "parse_profiles",
] as const;

type ChassisRow = {
  id: number;
  gigafy_device_name: string | null;
  adapter_key: string;
};

type PreviewChassis = {
  chassis_id: number;
  chassis_name: string | null;
  adapter_key: string;
  lines: string[];
  notes: string[];
};
type PreviewResponse = { chassis: PreviewChassis[] };

function confirmString(kind: string, count: number): string {
  return `bulk ${kind} on ${count} chassis`;
}

function BulkSubmitForm() {
  const qc = useQueryClient();
  const toast = useToast();
  const { confirm, confirmDialog } = useConfirm();
  const [kind, setKind] = useState<string>(COMMON_KINDS[0]);
  const [kindOverride, setKindOverride] = useState("");
  const [targetSet, setTargetSet] = useState<Set<number>>(new Set());
  const [paramsText, setParamsText] = useState("{}");
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [filter, setFilter] = useState("");

  const chassisQ = useQuery<{ chassis: ChassisRow[] }>({
    queryKey: ["v2", "chassis-list-bulk"],
    queryFn: async () => {
      const res = await api.get<{ chassis: ChassisRow[] }>("/chassis");
      return res.data;
    },
    staleTime: 60_000,
  });

  const effectiveKind = kind === "__other__" ? kindOverride.trim() : kind;
  const targets = useMemo(
    () => Array.from(targetSet).sort((a, b) => a - b),
    [targetSet],
  );
  const expectedConfirm = effectiveKind
    ? confirmString(effectiveKind, targets.length)
    : "";

  let parsedParams: Record<string, unknown> | null = null;
  let paramsError: string | null = null;
  try {
    const v = JSON.parse(paramsText || "{}");
    if (v && typeof v === "object" && !Array.isArray(v)) {
      parsedParams = v;
    } else {
      paramsError = "default_params must be a JSON object";
    }
  } catch (err) {
    paramsError = `JSON parse error: ${(err as Error).message}`;
  }

  const previewMut = useMutation({
    mutationFn: async () => {
      const res = await api.post<PreviewResponse>("/bulk-jobs/preview", {
        kind: effectiveKind,
        targets,
        default_params: parsedParams ?? {},
        per_chassis_params: {},
      });
      return res.data;
    },
    onSuccess: (data) => setPreview(data),
    onError: () => toast.push("Preview failed — check kind / params", "error"),
  });

  const submitMut = useMutation({
    mutationFn: async () => {
      // The server still gates on the confirm string (kind + target
      // count) — sent programmatically; the operator gate is the
      // two-click dialog.
      const res = await api.post<{ parent_job_id: string; queued: number }>(
        "/bulk-jobs",
        {
          kind: effectiveKind,
          targets,
          default_params: parsedParams ?? {},
          per_chassis_params: {},
          confirm: expectedConfirm,
        },
      );
      return res.data;
    },
    onSuccess: (data) => {
      toast.push(`Bulk job ${data.parent_job_id.slice(0, 8)}… queued`, "success");
      setPreview(null);
      setTargetSet(new Set());
      qc.invalidateQueries({ queryKey: ["v2", "bulk-jobs"] });
    },
    onError: () =>
      toast.push("Submit rejected — check kind / targets", "error"),
  });

  const filteredChassis = useMemo(() => {
    const rows = chassisQ.data?.chassis ?? [];
    const needle = filter.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter(
      (c) =>
        (c.gigafy_device_name ?? "").toLowerCase().includes(needle) ||
        c.adapter_key.toLowerCase().includes(needle) ||
        String(c.id).includes(needle),
    );
  }, [chassisQ.data, filter]);

  const allFiltered = filteredChassis.map((c) => c.id);
  const allFilteredSelected =
    allFiltered.length > 0 && allFiltered.every((id) => targetSet.has(id));

  const inputCls =
    "w-full rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-100 placeholder:text-slate-600 focus-visible:border-emerald-700 focus-visible:outline-none";

  const canPreview = effectiveKind.length > 0 && targets.length > 0 && !paramsError;

  const onSubmitClick = async () => {
    const ok = await confirm({
      title: "Submit bulk job",
      summary: expectedConfirm,
      children: preview ? (
        <CommandPreview
          text={preview.chassis
            .map(
              (c) =>
                `! ── chassis-${c.chassis_id} · ${c.chassis_name ?? "?"} ──\n` +
                c.lines.join("\n"),
            )
            .join("\n\n")}
        />
      ) : undefined,
      confirmLabel: "Submit bulk",
    });
    if (!ok) return;
    submitMut.mutate();
  };

  return (
    <section className="rounded-lg bg-slate-900 shadow-border p-4">
      <h2 className="mb-2 text-sm font-semibold text-slate-200">Submit a bulk job</h2>
      <p className="mb-3 text-[10px] text-slate-500">
        Fan a single job across many chassis. Submitting opens a
        two-click confirm showing the job kind + target count, so
        accidental fires are unlikely.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-slate-400">Kind</span>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            className={inputCls}
          >
            {COMMON_KINDS.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
            <option value="__other__">Other (type below)…</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-slate-400">Custom kind</span>
          <input
            type="text"
            value={kindOverride}
            onChange={(e) => setKindOverride(e.target.value)}
            disabled={kind !== "__other__"}
            placeholder={kind === "__other__" ? "e.g. mikrotik_pppoe_sync" : "(select Other…)"}
            className={inputCls + (kind !== "__other__" ? " opacity-40" : "")}
          />
        </label>
        <label className="sm:col-span-2 flex flex-col gap-1 text-xs">
          <span className="text-slate-400">
            Default params (JSON object) — merged into every child job
          </span>
          <textarea
            value={paramsText}
            onChange={(e) => setParamsText(e.target.value)}
            rows={3}
            spellCheck={false}
            className={inputCls + " font-mono text-[11px]"}
          />
          {paramsError && (
            <span className="text-[10px] text-red-400">{paramsError}</span>
          )}
        </label>
      </div>
      <div className="mt-3">
        <div className="mb-1 flex items-baseline justify-between text-xs">
          <span className="text-slate-400">
            Targets ({targets.length} selected)
          </span>
          <input
            type="search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="filter chassis…"
            className="w-64 rounded border border-slate-700 bg-slate-950 px-2 py-0.5 text-xs text-slate-200 placeholder:text-slate-600 focus-visible:border-emerald-700 focus-visible:outline-none"
          />
        </div>
        <div className="max-h-64 overflow-y-auto rounded border border-slate-800 bg-slate-950">
          {chassisQ.isLoading ? (
            <p className="px-2 py-1 text-xs text-slate-500">Loading chassis…</p>
          ) : filteredChassis.length === 0 ? (
            <p className="px-2 py-1 text-xs text-slate-500">No matches.</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-900 text-left text-[10px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-2 py-1 w-6">
                    <input
                      type="checkbox"
                      checked={allFilteredSelected}
                      onChange={() => {
                        setTargetSet((prev) => {
                          const next = new Set(prev);
                          if (allFilteredSelected) {
                            for (const id of allFiltered) next.delete(id);
                          } else {
                            for (const id of allFiltered) next.add(id);
                          }
                          return next;
                        });
                      }}
                    />
                  </th>
                  <th className="px-2 py-1">ID</th>
                  <th className="px-2 py-1">Name</th>
                  <th className="px-2 py-1">Adapter</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/40 text-slate-300">
                {filteredChassis.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() =>
                      setTargetSet((prev) => {
                        const next = new Set(prev);
                        if (next.has(c.id)) next.delete(c.id);
                        else next.add(c.id);
                        return next;
                      })
                    }
                    className={
                      "cursor-pointer " +
                      (targetSet.has(c.id) ? "bg-emerald-950/30" : "hover:bg-slate-900/50")
                    }
                  >
                    <td className="px-2 py-1">
                      <input
                        type="checkbox"
                        checked={targetSet.has(c.id)}
                        readOnly
                      />
                    </td>
                    <td className="px-2 py-1 font-mono">{c.id}</td>
                    <td className="px-2 py-1">{c.gigafy_device_name ?? "—"}</td>
                    <td className="px-2 py-1 font-mono text-[10px] text-slate-500">
                      {c.adapter_key}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => previewMut.mutate()}
          disabled={!canPreview || previewMut.isPending}
          className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-700 disabled:opacity-50"
        >
          <FileSearch className="h-3 w-3" />
          {previewMut.isPending ? "Rendering…" : "Preview"}
        </button>
        <span className="flex-1 min-w-[20rem] font-mono text-xs text-slate-500">
          {expectedConfirm || "select a kind + targets first"}
        </span>
        <button
          type="button"
          onClick={onSubmitClick}
          disabled={!canPreview || submitMut.isPending}
          className="inline-flex items-center gap-1 rounded border border-red-800 bg-red-900/30 px-3 py-1.5 text-xs text-red-200 hover:bg-red-900/50 disabled:opacity-50"
        >
          <Play className="h-3 w-3" />
          {submitMut.isPending ? "Submitting…" : "Submit bulk"}
        </button>
      </div>
      {preview && (
        <details open className="mt-3 rounded border border-slate-800 bg-slate-950/40">
          <summary className="cursor-pointer px-3 py-1.5 text-xs text-slate-400">
            Preview · {preview.chassis.length} chassis
          </summary>
          <div className="grid gap-2 px-3 py-2">
            {preview.chassis.map((c) => (
              <div key={c.chassis_id} className="rounded bg-slate-900 shadow-border p-2">
                <div className="mb-1 flex items-baseline justify-between text-[11px]">
                  <span className="font-mono text-slate-200">
                    chassis-{c.chassis_id} · {c.chassis_name ?? "?"}
                  </span>
                  <span className="text-[10px] text-slate-500">{c.adapter_key}</span>
                </div>
                {c.notes.length > 0 && (
                  <ul className="mb-1 text-[10px] text-amber-300">
                    {c.notes.map((n, i) => (
                      <li key={i}>· {n}</li>
                    ))}
                  </ul>
                )}
                {c.lines.length > 0 ? (
                  <pre className="max-h-32 overflow-auto rounded bg-slate-950 p-1.5 font-mono text-[10px] text-slate-300">
                    {c.lines.join("\n")}
                  </pre>
                ) : (
                  <p className="text-[10px] text-slate-500">(no lines rendered)</p>
                )}
              </div>
            ))}
          </div>
        </details>
      )}
      {confirmDialog}
    </section>
  );
}

export function BulkJobs() {
  const q = useQuery<BulkListResponse>({
    queryKey: ["v2", "bulk-jobs"],
    queryFn: async () => {
      const res = await api.get<BulkListResponse>("/bulk-jobs?limit=50");
      return res.data;
    },
    refetchInterval: 10_000,
  });
  const jobs = q.data?.bulk_jobs ?? [];
  return (
    <section className="flex flex-col gap-3">
      <header className="flex items-baseline justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-lg font-semibold text-slate-100">Bulk jobs</h1>
          <HelpButton topic="tools-bulk-jobs" />
        </div>
        <p className="text-xs text-slate-500">
          Fan-out a job across many chassis at once.
        </p>
      </header>
      <BulkSubmitForm />
      <div className="rounded-lg bg-slate-900 shadow-border p-4">
        {q.isLoading ? (
          <p className="text-xs text-slate-500">Loading…</p>
        ) : jobs.length === 0 ? (
          <p className="text-xs text-slate-500">No bulk jobs yet.</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-left text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-2 py-1">Status</th>
                <th className="px-2 py-1">Name</th>
                <th className="px-2 py-1">By</th>
                <th className="px-2 py-1">Targets</th>
                <th className="px-2 py-1">Started</th>
                <th className="px-2 py-1 text-right">Open</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/40 text-slate-300">
              {jobs.map((j) => (
                <tr key={j.parent_job_id}>
                  <td className="px-2 py-1.5">
                    <Badge tone={STATUS_TONE[j.status] ?? "neutral"}>
                      {j.status}
                    </Badge>
                  </td>
                  <td className="px-2 py-1.5 font-mono">{j.kind}</td>
                  <td className="px-2 py-1.5">{j.started_by_gigafy_username ?? "—"}</td>
                  <td className="px-2 py-1.5 font-mono">{j.total_targets ?? "—"}</td>
                  <td className="px-2 py-1.5 font-mono text-[10px] text-slate-500">
                    <LocalTime iso={j.started_at} />
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <Link
                      to={`/tools/bulk-jobs/${j.parent_job_id}`}
                      className="text-emerald-400 hover:text-emerald-300"
                    >
                      open
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

/** Bulk job detail — parent + children fan-out grid. */
export function BulkJobDetail({ parentId }: { parentId: string }) {
  const q = useQuery<BulkJobDetailView>({
    queryKey: ["v2", "bulk-jobs", parentId],
    queryFn: async () => {
      const res = await api.get<BulkJobDetailWire>(`/bulk-jobs/${parentId}`);
      return normaliseBulkJobDetail(res.data);
    },
    refetchInterval: 5_000,
  });
  const parent = q.data?.parent;
  const children = q.data?.children ?? [];
  return (
    <section className="flex flex-col gap-3">
      <header className="flex flex-wrap items-baseline gap-3">
        <h1 className="text-lg font-semibold text-slate-100">Bulk job</h1>
        <HelpButton topic="tools-bulk-jobs" />
        <CopyableCell value={parentId} />
        {parent && (
          <Badge tone={STATUS_TONE[parent.status] ?? "neutral"}>
            {parent.status}
          </Badge>
        )}
      </header>
      <div className="rounded-lg bg-slate-900 shadow-border p-4">
        {q.isLoading ? (
          <p className="text-xs text-slate-500">Loading…</p>
        ) : children.length === 0 ? (
          <p className="text-xs text-slate-500">No child jobs yet.</p>
        ) : (
          <ChildJobsTable rows={children} firstColumn="chassis" />
        )}
      </div>
    </section>
  );
}
